import {Course, IltSession} from "@lib/talentlms/talentlms-model";
import {useTimeZone} from "@lib/hooks/use-time-zone";
import {useNow} from "@lib/hooks/use-now";
import {
    defaultTimeZone,
    getFormattedDuration,
    getTimeZone,
    groupSessions,
    parseTalentLmsDate,
    sortSessions
} from "@lib/talentlms/talentlms-utils";
import {addMinutes, monthDiff} from "@lib/utils/date-utils";
import {Box, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow} from "@mui/material";
import Typography from "@mui/material/Typography";
import { Alert } from '@mui/material';
import React from "react";
import {EventBusy} from "@mui/icons-material";
import {useDateFormatter} from "react-aria";
import {TimeZoneFilter} from "@lib/training/time-zone-filter";
import Grid from "@mui/material/Grid";

export const CourseSchedule = ({
                                   course,
                                   iltSessions,
                                   timeZoneFilter,
                               }: { course: Course, iltSessions: IltSession[], timeZoneFilter: TimeZoneFilter }) => {
    const timeZone = useTimeZone()
    // Remove the parenthesis from the tile, used by Pricefx to indicate whether it's on demand or ILT
    const courseTitle = /(?<name>.*)\(.*/.exec(course.name)?.groups?.name ?? course.name

    const now = useNow()

    const sessionsToShow = groupSessions(iltSessions)
        .sort((sessions1, sessions2) => getFirstSession(sessions1).start_timestamp.localeCompare(getFirstSession(sessions2).start_timestamp))
        .filter(sessions => {
            const firstSession = getFirstSession(sessions)
            // Just used for filtering, so default value will not have an impact
            const timeZone = getTimeZone(firstSession)
            if (!timeZone) {
                return false
            }
            const includedByFilter = timeZoneFilter[timeZone]
            if (!includedByFilter) {
                return false
            }
            const startDate = parseTalentLmsDate(firstSession.start_date, timeZone)
            const monthsToStart = monthDiff(now, startDate)
            return monthsToStart >= -1
        })

    return (
        <Box my={4}>
            <Paper>
                <Box mt={8} mb={4} display='flex' justifyContent='center'>
                    <Typography variant='h2' id={course.id}>{courseTitle}</Typography>
                </Box>
                <Box my={4}>
                    <Typography>
                        <div dangerouslySetInnerHTML={{__html: course.description}}/>
                    </Typography>
                </Box>
                <Box my={4} display='flex' justifyContent='center'>
                    <Alert severity="info">
                        All times are listed in your time zone &lsquo;<em>{timeZone}</em>&rsquo;.
                    </Alert>
                </Box>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell align='right'>Start Date</TableCell>
                                <TableCell align='left'>End Date</TableCell>
                                <TableCell align='center'>Start Time</TableCell>
                                <TableCell align='center'>End Time</TableCell>
                                <TableCell align='center'>Taught from</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <SessionRows sessions={sessionsToShow}/>
                        </TableBody>
                    </Table>
                </TableContainer>
                <Box my={4} display='flex' justifyContent='center'>
                    <RegisterButtons course={course}/>
                </Box>
            </Paper>
        </Box>
    )
}

const RegisterButtons = ({course}: { course: Course }) => {
    const customerCourseUrl = `https://pricefx.talentlms.com/catalog/info/id:${course.id}`
    const partnerCourseUrl = `https://pricefx.talentlms.com/catalog/info/id:${course.id}`
    const internalCourseUrl = `https://pricefx.talentlms.com/catalog/info/id:${course.id}`
    const mainCourseUrl = `https://pricefx.talentlms.com/catalog/info/id:${course.id}`
    return (
        <Grid container spacing={4} justifyContent='center'>
            <Grid item>
                <Button href={mainCourseUrl} variant='outlined' color='primary'>Register</Button>
            </Grid>
        </Grid>
    );
}

const SessionRows = ({sessions}: { sessions: IltSession[][] }) => {
    return sessions.length === 0 ? (
        <TableRow><TableCell colSpan={4} align='center'>There are no planned events for this course.<span><EventBusy
            fontSize='small' style={{verticalAlign: "middle"}}/></span></TableCell></TableRow>
    ) : (<>{
        sessions.map(multiSessions => (
            <SessionRow key={multiSessions[0].id} sessions={multiSessions}/>
        ))
    }</>)
}
const SessionRow = ({sessions}: { sessions: IltSession[] }) => {
    const sortedSessions = sortSessions(sessions)
    const firstSession = sortedSessions[0]
    const lastSession = sortedSessions[sortedSessions.length - 1]
    const timeZone = getTimeZone(firstSession) ?? defaultTimeZone
    const firstDayStartDate = parseTalentLmsDate(firstSession.start_date, timeZone)
    const durationMinutes = parseInt(firstSession.duration_minutes)
    const durationString = getFormattedDuration(firstSession)
    const firstDayEndDate = addMinutes(firstDayStartDate, durationMinutes)
    const lastDayEndDate = parseTalentLmsDate(lastSession.start_date, timeZone)
    const dateFormatter = useDateFormatter({month: 'short', day: "numeric", year: 'numeric'})
    const timeFormatter = useDateFormatter({timeStyle: 'short'})
    // TODO add warning triangle if we cannot determine the time zone
    return (
        <TableRow>
            <TableCell align='right'>{dateFormatter.format(firstDayStartDate)}</TableCell>
            <TableCell align='left'>{dateFormatter.format(lastDayEndDate)}</TableCell>
            <TableCell align='center'>{timeFormatter.format(firstDayStartDate)}</TableCell>
            <TableCell align='center'>{timeFormatter.format(firstDayEndDate)}</TableCell>
            <TableCell align='center'>{timeZone}</TableCell>
        </TableRow>
    )
}
const getFirstSession = (sessions: IltSession[]) => (
    sortSessions(sessions)[0]
)